import React, { useEffect } from "react";
import { useObserver } from "mobx-react";
import locale from "@bonliva-crm/locale";
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText
} from "@material-ui/core";
import { DeleteOutline } from "mdi-material-ui";
import { useDeleteContactPersonDialog_GetQuery } from "@graphql";
import { useSelection } from "@hooks";
import { PrimaryButton, Spinner, SpinnerOverlay } from "@components";
import { useHandleDeassign } from "./useHandleDeassign";
import { CustomerProfileStore } from "../CustomerProfileStore";

export type Props = {
  store: CustomerProfileStore;
};

export function DeleteContactPersonDialog(props: Props) {
  const { store } = props;

  return useObserver(() => {
    if (!store.contactPersonToDelete) {
      return null;
    }

    return (
      <Dialog fullWidth onClose={() => store.closeDeleteContactDialog(false)} open>
        <DialogTitle>
          {locale.RemoveContactPerson(store.contactPersonToDelete.name)}
        </DialogTitle>

        {store.contactPersonToDelete && <Content store={store} />}
      </Dialog>
    );
  });
}

function Content(props: { store: CustomerProfileStore }) {
  const { store } = props;

  const { data, error, loading } = useDeleteContactPersonDialog_GetQuery({
    variables: {
      contactPersonId: store.contactPersonToDelete.id
    },
    fetchPolicy: "network-only"
  });

  const { loading: deassigning, deassign } = useHandleDeassign(
    store.contactPersonToDelete.id,
    () => store.closeDeleteContactDialog(true),
    store.contactPersonToDelete.name
  );
  const selection = useSelection();

  useEffect(() => {
    if (data?.contactPersonById) {
      selection.reset(data.contactPersonById.Customers.map((x) => x._id));
      selection.select(store.id);
    }
  }, [data?.contactPersonById]);

  if (error) {
    throw error;
  }

  return useObserver(() => {
    if (loading || !data || !data.contactPersonById) {
      return <Spinner />;
    }

    return (
      <>
        <DialogContent style={{ padding: 0 }}>
          <SpinnerOverlay show={deassigning} />

          <List>
            <ListItem button onClick={selection.toggleAll}>
              <Checkbox checked={selection.allSelected} />
              <ListItemText primary={"All"} />
            </ListItem>

            {data.contactPersonById.Customers.map((customer) => {
              return (
                <ListItem
                  key={customer._id}
                  button
                  onClick={() => selection.toggle(customer._id)}
                >
                  <Checkbox checked={selection.isSelected(customer._id)} />
                  <ListItemText
                    primary={customer.Name}
                    secondary={customer.Parents[1]?.Name}
                  />
                </ListItem>
              );
            })}
          </List>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => store.closeDeleteContactDialog(false)} color="primary">
            {locale.Cancel}
          </Button>

          <PrimaryButton
            leftIcon={selection.allSelected ? <DeleteOutline /> : undefined}
            disabled={selection.empty}
            onClick={() => deassign(selection.selected, selection.items)}
            color="primary"
          >
            {selection.allSelected ? locale.Delete : locale.Deassign}
          </PrimaryButton>
        </DialogActions>
      </>
    );
  });
}
